import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Button, ColorPicker, Field, KanbanTag, Modal, ModalActions } from '@evogard/ui';
import type { DeskLabelOption } from '@evogard/contract';
import { LABEL_NAME_MAX, type LabelColor } from '@evogard/domain';
import { api, ok } from '../../../lib/api.js';
import { DESK_KEY, useInvalidateDesk } from './useDesk.js';

/** A chave da lista de etiquetas da Mesa de uma base — o card e esta janela leem a mesma. */
export const deskLabelsKey = (tenant: string) => [...DESK_KEY, 'etiquetas', tenant];

/**
 * As etiquetas da Mesa de uma base (27/10/2026) — o "Gerenciar etiquetas" do
 * Kanban.
 *
 * São da equipe da cobrança inteira: qualquer operador cria, e a etiqueta
 * aparece para todos da base. Renomear, trocar a cor e arquivar é de quem
 * criou, ou de quem configura a Mesa — `canEdit` vem do servidor, que confere
 * de novo.
 *
 * **Arquivar tira a etiqueta de todos os cards**, como apagar no Kanban; por
 * isso pede confirmação na própria linha. O operador só arquiva a dele se ela
 * não estiver no card de outra pessoa — o servidor recusa e a mensagem aparece
 * aqui (decisão do dono, 27/10/2026).
 */
export function DeskLabelsModal({ tenant, onClose }: { tenant: string; onClose: () => void }) {
  const invalidate = useInvalidateDesk();
  const labels = useQuery({
    queryKey: deskLabelsKey(tenant),
    queryFn: async () =>
      ok(await api.billingDesk.labelOptions({ params: { tenant } }), 200, 'Não foi possível carregar as etiquetas.'),
  });

  const archive = useMutation({
    mutationFn: async (label: DeskLabelOption) =>
      ok(
        await api.billingDesk.archiveLabel({ params: { tenant, id: label.id }, body: {} }),
        200,
        'Não foi possível arquivar a etiqueta.',
      ),
    onSuccess: () => {
      setConfirming(null);
      return invalidate();
    },
  });

  /** A etiqueta em edição na linha, e a que espera o "sim" de arquivar; `null` é nenhuma. */
  const [editing, setEditing] = useState<number | null>(null);
  const [confirming, setConfirming] = useState<number | null>(null);
  const list = labels.data?.items ?? [];

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Etiquetas da Mesa"
      descricao="As etiquetas da equipe da cobrança desta base. Quem cria renomeia a dela, e arquiva se ela não estiver no card de outra pessoa."
      icon="etiqueta"
      tone="destaque"
      size="md"
    >
      <div className="flex flex-col gap-5">
        <LabelForm tenant={tenant} submitLabel="Criar etiqueta" onSaved={invalidate} />

        {labels.isError && <Alert tone="erro">{labels.error.message}</Alert>}
        {archive.isError && <Alert tone="erro">{archive.error.message}</Alert>}

        <section aria-label="Etiquetas da Mesa" className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold tracking-wide text-texto-tenue uppercase">
            {labels.data ? `${list.length} etiqueta(s)` : 'Carregando…'}
          </h3>
          {labels.data && list.length === 0 && (
            <p className="text-sm text-texto-suave">Nenhuma etiqueta ainda. Crie a primeira acima.</p>
          )}
          <ul className="flex flex-col divide-y divide-borda-suave">
            {list.map((label) =>
              editing === label.id ? (
                <li key={label.id} className="py-3">
                  <LabelForm
                    tenant={tenant}
                    label={label}
                    submitLabel="Salvar"
                    onSaved={async () => {
                      await invalidate();
                      setEditing(null);
                    }}
                    onCancel={() => setEditing(null)}
                  />
                </li>
              ) : (
                <li key={label.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <KanbanTag color={label.color}>{label.name}</KanbanTag>
                  {label.canEdit &&
                    (confirming === label.id ? (
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-texto-suave">Tirar de todos os cards?</span>
                        <Button size="sm" variant="secundario" onClick={() => setConfirming(null)}>
                          Não
                        </Button>
                        <Button
                          size="sm"
                          variant="perigo"
                          loading={archive.isPending && archive.variables?.id === label.id}
                          onClick={() => archive.mutate(label)}
                        >
                          Arquivar
                        </Button>
                      </span>
                    ) : (
                      <span className="flex gap-1">
                        <Button size="sm" variant="discreto" onClick={() => setEditing(label.id)}>
                          Editar
                        </Button>
                        <Button size="sm" variant="discreto" onClick={() => setConfirming(label.id)}>
                          Arquivar
                        </Button>
                      </span>
                    ))}
                </li>
              ),
            )}
          </ul>
        </section>

        <ModalActions className="border-t border-borda-suave pt-5">
          <Button variant="secundario" onClick={onClose}>
            Fechar
          </Button>
        </ModalActions>
      </div>
    </Modal>
  );
}

/** Criar (sem `label`) ou editar uma etiqueta da Mesa: o nome, a cor e a prévia. */
function LabelForm({
  tenant,
  label,
  submitLabel,
  onSaved,
  onCancel,
}: {
  tenant: string;
  label?: DeskLabelOption;
  submitLabel: string;
  onSaved: () => Promise<unknown>;
  onCancel?: () => void;
}) {
  const [name, setName] = useState(label?.name ?? '');
  const [color, setColor] = useState<LabelColor>(label?.color ?? 'azul');

  const save = useMutation({
    mutationFn: async () => {
      const body = { name: name.trim(), color };
      return label
        ? ok(
            await api.billingDesk.editLabel({ params: { tenant, id: label.id }, body }),
            200,
            'Não foi possível salvar a etiqueta.',
          )
        : ok(await api.billingDesk.createLabel({ params: { tenant }, body }), 201, 'Não foi possível criar a etiqueta.');
    },
    onSuccess: async () => {
      await onSaved();
      if (!label) setName('');
    },
  });

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) save.mutate();
      }}
    >
      <Field
        label={label ? 'Nome da etiqueta' : 'Nova etiqueta'}
        required
        autoFocus={Boolean(label)}
        maxLength={LABEL_NAME_MAX}
        placeholder="Ex.: Aguardando retorno"
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={save.error?.message}
      />
      <ColorPicker value={color} onChange={setColor} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span aria-hidden className="flex items-center gap-2 text-xs text-texto-suave">
          Prévia: <KanbanTag color={color}>{name.trim() || 'Etiqueta'}</KanbanTag>
        </span>
        <span className="flex gap-2">
          {onCancel && (
            <Button type="button" size="sm" variant="secundario" onClick={onCancel}>
              Cancelar
            </Button>
          )}
          <Button type="submit" size="sm" loading={save.isPending} disabled={name.trim() === ''}>
            {submitLabel}
          </Button>
        </span>
      </div>
    </form>
  );
}
